import type { Dispatch, FC, SetStateAction } from 'react';
import type React from 'react';
import { createContext, useContext, useMemo, useRef, useState } from 'react';

import type {
  CommentTopic,
  DashboardComment,
  SentimentCode,
} from 'src/types/dashboard';
import useDebounce, { debounceTimes } from 'src/hooks/useDebounce';
import ErrorHandler from 'src/components/common/ErrorHandler';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import { useTagCloud } from 'src/components/dashboard/DashboardContext/TagCloudContext';
import useHRQSCommentsQuery from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/HRQSOpenCommentsWidget/useHRQSCommentsQuery';
import useCommentsQuery from 'src/components/dashboard/hooks/useCommentsQuery';
import { TagSentimentFilter } from 'src/components/dashboard/hooks/useTagsQuery';

export interface ApiComment {
  text: string;
  translated_text: string;
  sentiment_overall: SentimentCode;
  topics: Array<CommentTopic>;
}

export interface DashboardCommentValue {
  comments: Array<DashboardComment>;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setCommentIds: Dispatch<SetStateAction<Array<string>>>;
  loading: boolean;
  setQuestion: Dispatch<SetStateAction<string | null>>;
  question: string | null;
  textFilter: string;
  textFilterLoading: boolean;
  updateTextFilter: (text: string) => void;
  totalPageCount: number;
  widgetId?: number;
  source: 'topics' | 'token' | 'hrqs';
}

export const DashboardCommentsContext = createContext<DashboardCommentValue>({
  comments: [],
  currentPage: 0,
  setCurrentPage: () => {},
  setCommentIds: () => {},
  loading: false,
  setQuestion: () => {},
  question: null,
  textFilter: '',
  textFilterLoading: false,
  updateTextFilter: () => {},
  totalPageCount: 1,
  widgetId: 0,
  source: 'topics',
});

interface DashboardCommentProviderProps {
  /**
   * @type string
   * Defines the type of data being requested from the reporting engine
   * topics = eos open comments
   * token = eos tag cloud
   * hrqs = hrqs open comments
   */
  source: 'topics' | 'token' | 'hrqs';
  children: React.ReactNode;
  widgetId?: number; // required for hrqs
}

const DashboardCommentsProvider: FC<DashboardCommentProviderProps> = ({
  source,
  children,
  widgetId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [commentIds, setCommentIds] = useState<Array<string>>([]);
  const [question, setQuestion] = useState<string | null>(null);
  const [textFilter, setTextFilter] = useState('');
  const debouncedTextFilter = useDebounce(
    textFilter,
    debounceTimes.TEXT_FILTER,
  );
  const updateTextFilter = (text: string) => {
    setCurrentPage(1);
    setTextFilter(text);
  };

  const { sentimentFilter } = useTagCloud();
  const commentsRef = useRef<DashboardComment[]>([]);
  const totalPageCountRef = useRef<number>(1);

  const { dashboardConfig } = useDashboardContext();
  const { id: dashboardId } = dashboardConfig || {};

  const {
    data: dashboardData,
    isFetching: dashboardIsFetching,
    isLoading: dashboardIsLoading,
    error: dashboardError,
  } = useCommentsQuery(
    source,
    commentIds.map((id) => String(id)),
    question,
    currentPage,
    debouncedTextFilter,
    sentimentFilter === TagSentimentFilter.ALL ? null : sentimentFilter,
    dashboardId,
  );
  const {
    data: hrqsData,
    isFetching: hrqsIsFetching,
    isLoading: hrqsIsLoading,
    error: hrqsError,
  } = useHRQSCommentsQuery(
    source,
    commentIds.map((id) => String(id)),
    currentPage,
    debouncedTextFilter,
    widgetId,
  );

  const loading =
    source === 'hrqs'
      ? hrqsIsFetching || hrqsIsLoading
      : dashboardIsLoading || dashboardIsFetching;
  const data = source === 'hrqs' ? hrqsData : dashboardData;
  const error = source === 'hrqs' ? hrqsError : dashboardError;
  const textFilterLoading = textFilter !== debouncedTextFilter;

  const [comments, totalPageCount] = useMemo(() => {
    const [newData, totalPageCount] = data || [[], totalPageCountRef.current];
    totalPageCountRef.current = totalPageCount;
    if (currentPage !== 1) {
      commentsRef.current = [...commentsRef.current, ...newData];
      return [commentsRef.current, totalPageCount] as [
        DashboardComment[],
        number,
      ];
    }
    commentsRef.current = newData;
    return [newData, totalPageCount] as [DashboardComment[], number];
  }, [data]);
  return (
    <DashboardCommentsContext.Provider
      value={{
        comments,
        currentPage,
        totalPageCount,
        loading,
        setQuestion,
        question,
        textFilter,
        textFilterLoading,
        updateTextFilter,
        setCurrentPage,
        setCommentIds,
        source,
        widgetId,
      }}
    >
      {children}
      <ErrorHandler errorResponse={error} />
    </DashboardCommentsContext.Provider>
  );
};
export const useDashboardCommentContext = () =>
  useContext(DashboardCommentsContext);

export default DashboardCommentsProvider;
